import { createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';

import type {
  CadastroAdvogadoConviteInput,
  ConviteAdvogadoAceiteResultado,
  ConviteAdvogadoErro,
  ConviteAdvogadoPreview,
  MarcoAdvogado,
  NotificacaoAdvogado,
  PortalAdvogadoDashboard,
  PortalAdvogadoScenario,
  ProcessoAdvogadoAccessResult,
  ProcuracaoUploadInput,
  ProcuracaoUploadResult,
} from '../types';
import { portalAdvogadoService, PortalAdvogadoApiError } from './services';

const paraConviteErro = (error: unknown): ConviteAdvogadoErro => {
  if (error instanceof PortalAdvogadoApiError) {
    return { codigo: error.codigo, mensagem: error.message };
  }
  return { codigo: 'ERRO_REDE', mensagem: 'Sua conexão foi interrompida. Tente novamente.' };
};

export const carregarConviteAdvogado = createAsyncThunk<
  ConviteAdvogadoPreview,
  string,
  { rejectValue: ConviteAdvogadoErro }
>('portalAdvogado/carregarConvite', async (token, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.previewConvite(token);
  } catch (error) {
    return rejectWithValue(paraConviteErro(error));
  }
});

export const concluirCadastroAdvogado = createAsyncThunk<
  ConviteAdvogadoAceiteResultado,
  CadastroAdvogadoConviteInput,
  { rejectValue: ConviteAdvogadoErro }
>('portalAdvogado/concluirCadastro', async (input, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.aceitarConvite(input);
  } catch (error) {
    return rejectWithValue(paraConviteErro(error));
  }
});

export const carregarPortalAdvogado = createAsyncThunk<
  PortalAdvogadoDashboard,
  void,
  { rejectValue: string }
>('portalAdvogado/carregarDashboard', async (_, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.obterDashboard();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarProcessoAdvogado = createAsyncThunk<
  ProcessoAdvogadoAccessResult,
  string,
  { rejectValue: string }
>('portalAdvogado/carregarProcesso', async (processoId, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.obterProcesso(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const enviarProcuracaoAdvogado = createAsyncThunk<
  ProcuracaoUploadResult,
  ProcuracaoUploadInput,
  { rejectValue: string }
>('portalAdvogado/enviarProcuracao', async (input, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.enviarProcuracao(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarNotificacoesAdvogado = createAsyncThunk<
  NotificacaoAdvogado[],
  string,
  { rejectValue: string }
>('portalAdvogado/carregarNotificacoes', async (escritorioId, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.obterNotificacoes(escritorioId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const marcarNotificacoesLidasAdvogado = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('portalAdvogado/marcarNotificacoesLidas', async (escritorioId, { rejectWithValue }) => {
  try {
    await portalAdvogadoService.marcarNotificacoesLidas(escritorioId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const carregarCalendarioAdvogado = createAsyncThunk<
  MarcoAdvogado[],
  PortalAdvogadoScenario,
  { rejectValue: string }
>('portalAdvogado/carregarCalendario', async (scenario, { rejectWithValue }) => {
  try {
    return await portalAdvogadoService.obterCalendario(scenario);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});
